import { DestroyRef, DOCUMENT, inject, Service } from '@angular/core'
import { Location } from '@angular/common'
import { NavigationError, NavigationStart, RedirectCommand, Router } from '@angular/router'
import { isChunkLoadError, LoggerService, MetaApiService, StaleBuildReloader } from '@seiri/shared/frontend'
import { environment } from '../../../environments/environment'
import { staleChunkFallback } from './stale-chunk-fallback'

/** sessionStorage key holding the server version this tab already reloaded for. */
const RELOADED_FOR_VERSION_KEY = 'seiri:stale-reload-version'

/** How often coming back to the tab may ask the server for its version. */
const CHECK_INTERVAL_MS = 5 * 60_000

/**
 * Notices that a deploy happened while this tab was open, by comparing the server's version with
 * the build this page runs, and moves the tab onto the new build at the next navigation - before
 * a lazy route can fail on a chunk that no longer exists. A navigation, not a timer, so nothing
 * the user is typing is thrown away. See docs/web-releases.md.
 */
@Service()
export class StaleBuildService {
  private readonly logger = inject(LoggerService).forContext('StaleBuildService')
  private readonly metaApi = inject(MetaApiService)
  private readonly reloader = inject(StaleBuildReloader)
  private readonly router = inject(Router)
  private readonly location = inject(Location)
  private readonly document = inject(DOCUMENT)
  private readonly destroyRef = inject(DestroyRef)

  /** The server's version, once it is known to differ from this build's. */
  private serverVersion: string | null = null
  private lastCheckAt = 0

  start(): void {
    if (environment.buildCommit === 'local') {
      return
    }
    void this.check()

    const onVisibilityChange = () => {
      if (this.document.visibilityState === 'visible') {
        void this.check()
      }
    }
    this.document.addEventListener('visibilitychange', onVisibilityChange)
    const navigation = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.reloadIfStale(event.url)
      }
    })
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('visibilitychange', onVisibilityChange)
      navigation.unsubscribe()
    })
  }

  private async check(): Promise<void> {
    const now = Date.now()
    if (this.serverVersion || now - this.lastCheckAt < CHECK_INTERVAL_MS) {
      return
    }
    this.lastCheckAt = now
    try {
      const { version } = await this.metaApi.getVersion()
      if (version && version !== environment.buildCommit) {
        this.logger.info('newer build deployed, reloading at the next navigation', {
          clientVersion: environment.buildCommit,
          serverVersion: version,
        })
        this.serverVersion = version
      }
    } catch (error) {
      // Offline or the API is restarting mid-deploy; the next visit to the tab asks again.
      this.logger.debug('version check failed', { error: String(error) })
    }
  }

  private reloadIfStale(url: string): void {
    const serverVersion = this.serverVersion
    if (!serverVersion) {
      return
    }
    // Once per server version: if the reload still lands on an older build (web and API deployed
    // out of step), reloading again would only turn every navigation into a full page load.
    if (readSession(this.document, RELOADED_FOR_VERSION_KEY) === serverVersion) {
      this.logger.warn('build still differs from the server after reloading, continuing', {
        clientVersion: environment.buildCommit,
        serverVersion,
      })
      this.serverVersion = null
      return
    }
    if (this.reloader.reloadTo(this.location.prepareExternalUrl(url))) {
      writeSession(this.document, RELOADED_FOR_VERSION_KEY, serverVersion)
    }
  }
}

/**
 * Router navigation error handler. A lazy chunk that fails to load means the tab runs a build the
 * server no longer has: do one full page load to where the user was going instead of leaving
 * every lazy route broken until a hard refresh.
 */
export function recoverFromStaleChunk(error: NavigationError): RedirectCommand | undefined {
  if (!isChunkLoadError(error.error)) {
    return undefined
  }
  const logger = inject(LoggerService).forContext('StaleBuild')
  const router = inject(Router)
  const reloader = inject(StaleBuildReloader)
  const url = inject(Location).prepareExternalUrl(error.url)

  if (reloader.isReloading()) {
    // The version check already started the reload; this navigation was racing it.
  } else if (reloader.reloadTo(url)) {
    logger.warn('lazy chunk missing, reloading into the current build', { url })
  } else {
    // Reloaded moments ago and it failed again: not a stale tab but a broken deploy.
    logger.error('lazy chunk missing after reloading', error.error, { url })
  }
  // Stay where we are rather than rethrow: either the page is about to be replaced, or the failure
  // has just been reported and a rethrow would only report it a second time. A rethrow on the first
  // navigation would also rewrite the address bar to `/`.
  const fallback = staleChunkFallback(router.navigated, router.url)
  return new RedirectCommand(router.parseUrl(fallback.url), { skipLocationChange: fallback.skipLocationChange })
}

function readSession(document: Document, key: string): string | null {
  try {
    return document.defaultView?.sessionStorage.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeSession(document: Document, key: string, value: string): void {
  try {
    document.defaultView?.sessionStorage.setItem(key, value)
  } catch {
    // Without storage the version guard is lost; the reloader's own time guard still holds.
  }
}
